import { computed, getCurrentScope, onScopeDispose, ref, watch, type Ref } from 'vue'
import { useConvexQuery } from 'convex-vue'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'

// Matches the dashboard's staleness window (convex/chatRooms.ts). Typers ping
// every ~2s, so a gap this long means they stopped without clearing.
export const TYPING_STALE_AFTER_MS = 5000

export type ChatSide = 'agent' | 'client'

type SideActivity = {
  typingAt: number | null
  seenMessageTimestamp: number | null
  seenAt: number | null
}

/**
 * Whether the other side's typingAt is still fresh at `now`.
 */
export function isTypingFresh(typingAt: number | null | undefined, now: number): boolean {
  return !!typingAt && now - typingAt < TYPING_STALE_AFTER_MS
}

/**
 * The Seen receipt for `message` - only when the other side has read up to
 * (or past) it. Returns the time they saw it, or null when it's only Sent.
 */
export function getSeenAt(
  message: { timestamp: number } | null | undefined,
  counterpart: SideActivity | null | undefined
): number | null {
  if (!message || !counterpart?.seenMessageTimestamp || !counterpart.seenAt) return null
  return counterpart.seenMessageTimestamp >= message.timestamp ? counterpart.seenAt : null
}

/**
 * Typing and Seen state of the other side of a support conversation, as seen
 * by `viewerSide` (agents watch the customer, the widget watches the agent).
 * The query returns typingAt raw, and a Convex query doesn't re-run just
 * because time passed - so a local clock ticks while someone is typing to
 * drop the indicator once it goes stale.
 */
export function useRoomActivity(roomId: Ref<string | null | undefined>, viewerSide: ChatSide) {
  const { data: activity } = useConvexQuery(
    api.chatRooms.getRoomActivity,
    computed(() => roomId.value ? { roomId: roomId.value as Id<'chatRooms'> } : undefined)
  )

  const counterpart = computed<SideActivity | null>(() => {
    if (!activity.value) return null
    return viewerSide === 'agent' ? activity.value.client : activity.value.agent
  })

  const now = ref(Date.now())
  let ticker: ReturnType<typeof setInterval> | null = null
  const stopTicker = () => {
    if (ticker) {
      clearInterval(ticker)
      ticker = null
    }
  }

  watch(() => counterpart.value?.typingAt, (typingAt) => {
    now.value = Date.now()
    if (isTypingFresh(typingAt, now.value)) {
      if (!ticker) ticker = setInterval(() => {
        now.value = Date.now()
        if (!isTypingFresh(counterpart.value?.typingAt, now.value)) stopTicker()
      }, 1000)
    } else {
      stopTicker()
    }
  }, { immediate: true })

  if (getCurrentScope()) onScopeDispose(stopTicker)

  const isCounterpartTyping = computed(() => isTypingFresh(counterpart.value?.typingAt, now.value))

  return { counterpart, isCounterpartTyping }
}
